import { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Loader2, CheckCircle2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { errMsg, fmtBytes } from "@/lib/api";
import { portalApi } from "@/lib/portal";

const MAX = 100 * 1024 * 1024;
const ACCEPT = ".jpg,.jpeg,.png,.gif,.webp,.heic,.mp4,.mov,.webm,.m4v,.avi,.pdf,.zip";

export const PortalUpload = ({ token }) => {
  const [files, setFiles] = useState([]);
  const [nota, setNota] = useState("");
  const [progress, setProgress] = useState(null);
  const [done, setDone] = useState([]);
  const ref = useRef();

  const pick = (list) => {
    const ok = list.filter((f) => (f.size > MAX ? (toast.error(`«${f.name}» supera i 100 MB`), false) : true));
    setFiles((p) => [...p, ...ok]);
  };

  const send = async () => {
    const batch = crypto.randomUUID();
    const sent = [];
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file); fd.append("batch_id", batch); fd.append("nota", nota);
      setProgress({ name: file.name, pct: 0 });
      try {
        await portalApi.post(`/${token}/upload`, fd, { onUploadProgress: (e) => setProgress({ name: file.name, pct: Math.round((e.loaded / (e.total || 1)) * 100) }) });
        sent.push(file.name);
      } catch (e) { toast.error(`«${file.name}»: ${errMsg(e, "caricamento non riuscito")}`); }
    }
    if (sent.length) {
      try { await portalApi.post(`/${token}/upload/completa`, { batch_id: batch }); } catch { /* notification is best-effort */ }
      toast.success(`${sent.length} file inviati all'agenzia`);
    }
    setDone(sent); setFiles([]); setNota(""); setProgress(null);
  };

  return (
    <div className="space-y-5" data-testid="portal-upload">
      <div onClick={() => !progress && ref.current.click()} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); pick([...e.dataTransfer.files]); }}
        className="cursor-pointer rounded-2xl border-2 border-dashed border-[#0A1128]/30 bg-[#8FD0FB] hover:border-[#E8173A] px-8 py-12 text-center transition-colors" data-testid="portal-dropzone">
        <input ref={ref} type="file" multiple accept={ACCEPT} className="hidden" data-testid="portal-file-input" onChange={(e) => { pick([...e.target.files]); e.target.value = ""; }} />
        <UploadCloud className="h-9 w-9 text-[#0A1128] mx-auto mb-3" />
        <div className="font-display text-lg font-extrabold text-[#0A1128]">Trascina qui i file o clicca per sceglierli</div>
        <div className="text-sm text-[#0A1128]/70 mt-1">Immagini, video, PDF o ZIP · max 100 MB per file</div>
      </div>
      {files.length > 0 && (
        <ul className="rounded-2xl bg-[#8FD0FB] border border-[#5FB3EA] divide-y divide-[#5FB3EA]/60" data-testid="portal-selected-files">
          {files.map((f, i) => <li key={i} className="px-5 py-3 flex justify-between text-sm text-[#0A1128]"><span className="font-semibold truncate">{f.name}</span><span>{fmtBytes(f.size)}</span></li>)}
        </ul>
      )}
      <Textarea rows={3} value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Nota per l'agenzia (facoltativa)" className="rounded-xl" data-testid="portal-upload-note" />
      {progress && <div data-testid="portal-upload-progress"><div className="text-sm font-bold text-[#0A1128] mb-1 truncate">Caricamento di «{progress.name}»...</div><Progress value={progress.pct} className="h-2" /></div>}
      <Button disabled={!files.length || !!progress} onClick={send} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] font-bold px-6" data-testid="portal-upload-submit">
        {progress ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <UploadCloud className="h-4 w-4 mr-1.5" />}Invia {files.length || ""} file
      </Button>
      {done.length > 0 && <div className="flex items-center gap-2 text-sm font-bold text-emerald-800" data-testid="portal-upload-done"><CheckCircle2 className="h-4 w-4" />Ricevuti: {done.join(", ")}</div>}
    </div>
  );
};
